<template>
  <div class="space-y-4">
    <div
      class="border-2 border-dashed border-nirvana-blue/20 rounded-lg p-8 text-center"
      :class="{ 'bg-nirvana-blue/5': isDragging }"
      @dragenter.prevent="isDragging = true"
      @dragleave.prevent="isDragging = false"
      @dragover.prevent
      @drop.prevent="handleDrop"
      @click.stop
    >
      <div v-if="!preview" class="space-y-2">
        <div class="flex justify-center">
          <FileIcon class="w-12 h-12 text-nirvana-blue/40" />
        </div>
        <p class="text-gray-600">
          ลากและวางไฟล์ของคุณที่นี่ หรือ
          <button
            type="button"
            @click.stop="triggerFileInput"
            class="text-nirvana-blue hover:text-nirvana-light-blue transition-colors"
          >
            เลือกไฟล์
          </button>
        </p>
        <p class="text-sm text-gray-500">
          รองรับไฟล์ประเภททั้งหมด
        </p>
      </div>

      <div v-else class="text-center">
        <div class="flex justify-center mb-2">
          <FileIcon class="w-12 h-12 text-nirvana-blue/60" />
        </div>
        <p class="font-medium">{{ selectedFile.name }}</p>
        <p class="text-sm text-gray-500">
          {{ formatFileSize(selectedFile.size) }}
        </p>
      </div>

      <input
        ref="fileInput"
        type="file"
        class="hidden"
        @change="handleFileSelect"
      />
    </div>

    <div v-if="preview" class="flex justify-end space-x-2">
      <button
        type="button"
        @click.stop="clearFile"
        class="text-gray-500 hover:text-gray-700 transition-colors"
      >
        Clear
      </button>
    </div>

    <p v-if="error" class="text-red-500 text-sm">{{ error }}</p>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { FileIcon } from 'lucide-vue-next'
import { uploadsAPI } from '../services/api'

const props = defineProps({
  disabled: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['fileSelected', 'update:file'])
const fileInput = ref(null)
const preview = ref(null)
const error = ref(null)
const isDragging = ref(false)
const selectedFile = ref(null)

function triggerFileInput(e) {
  e.preventDefault()
  e.stopPropagation()
  if (!props.disabled) {
    fileInput.value.click()
  }
}

function handleFileSelect(event) {
  event.preventDefault()
  event.stopPropagation()
  const file = event.target.files[0]
  processFile(file)
}

function handleDrop(event) {
  event.preventDefault()
  event.stopPropagation()
  isDragging.value = false
  const file = event.dataTransfer.files[0]
  processFile(file)
}

function processFile(file) {
  if (!file) return

  error.value = null
  preview.value = true
  selectedFile.value = file
  emit('update:file', file)
  emit('fileSelected', file)
}

function clearFile(e) {
  e.preventDefault()
  e.stopPropagation()
  preview.value = null
  selectedFile.value = null
  error.value = null
  emit('update:file', null)
  emit('fileSelected', null)
  if (fileInput.value) {
    fileInput.value.value = ''
  }
}

function formatFileSize(size) {
  if (size < 1024) {
    return size + ' bytes'
  } else if (size < 1048576) {
    return (size / 1024).toFixed(1) + ' KB'
  } else if (size < 1073741824) {
    return (size / 1048576).toFixed(1) + ' MB'
  } else {
    return (size / 1073741824).toFixed(1) + ' GB'
  }
}

const uploadFile = async () => {
  if (!selectedFile.value) {
    error.value = 'No file selected'
    return null
  }

  try {
    const result = await uploadsAPI.uploadMedia(selectedFile.value)
    return result.url
  } catch (err) {
    error.value = err.message || 'Failed to upload file'
    return null
  }
}

function reset() {
  preview.value = null
  selectedFile.value = null
  error.value = null
  if (fileInput.value) {
    fileInput.value.value = ''
  }
}

defineExpose({
  uploadFile,
  reset
})
</script>
